import { useEffect, useRef, useMemo, useCallback, memo, useState } from 'react'
import { useSession, useSettings, useToast } from '../context'
import { formatTimestamp, convertToIST, parseAnsiColors, copyToClipboard } from '../utils'

const VIRTUALIZATION_THRESHOLD = 300
const ROW_HEIGHT = 48
const BUFFER_ROWS = 8

export default memo(function LogViewer({ 
  searchTerm, 
  levelFilters, 
  autoScroll,
  setAutoScroll 
}) {
  const { logs, isLoading, isLoadingMore, currentSessionId, hasMore, loadMoreLogs } = useSession()
  const { settings } = useSettings()
  const { showToast } = useToast()
  const containerRef = useRef(null)
  const userIsScrollingRef = useRef(false)
  const isFetchingOlderRef = useRef(false)
  const scrollTimeoutRef = useRef(null)
  const scrollRafRef = useRef(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [containerHeight, setContainerHeight] = useState(0)

  // Filter logs
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const level = log.level || 'OTHER'
      if (!levelFilters.has(level)) return false
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        return log.message.toLowerCase().includes(term)
      }
      return true
    })
  }, [logs, levelFilters, searchTerm])

  // Simple virtualization for large datasets
  const { visibleLogs, paddingTop, paddingBottom } = useMemo(() => {
    if (filteredLogs.length < VIRTUALIZATION_THRESHOLD) {
      return { visibleLogs: filteredLogs, paddingTop: 0, paddingBottom: 0 }
    }
    const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_ROWS)
    const visibleCount = Math.ceil(containerHeight / ROW_HEIGHT) + BUFFER_ROWS * 2
    const endIndex = Math.min(filteredLogs.length, startIndex + visibleCount)
    return {
      visibleLogs: filteredLogs.slice(startIndex, endIndex),
      paddingTop: startIndex * ROW_HEIGHT,
      paddingBottom: Math.max(0, (filteredLogs.length - endIndex) * ROW_HEIGHT)
    }
  }, [filteredLogs, scrollTop, containerHeight])

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: containerRef.current.scrollHeight })
    }
  }, [])

  // Handle scroll events with automatic infinite upward scrolling
  const handleScroll = useCallback(async (e) => {
    const container = e.target

    // Auto-fetch older logs when scrolling near the top
    if (container.scrollTop < 120 && hasMore && !isFetchingOlderRef.current && !isLoadingMore) {
      isFetchingOlderRef.current = true
      const prevScrollHeight = container.scrollHeight
      const prevScrollTop = container.scrollTop

      await loadMoreLogs()

      // Preserve scroll position so view doesn't jump
      requestAnimationFrame(() => {
        if (container) {
          const heightDiff = container.scrollHeight - prevScrollHeight
          container.scrollTop = prevScrollTop + heightDiff
        }
        isFetchingOlderRef.current = false
      })
    }

    if (filteredLogs.length >= VIRTUALIZATION_THRESHOLD) {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current)
      }
      scrollRafRef.current = requestAnimationFrame(() => {
        setScrollTop(container.scrollTop)
      })
    }

    if (!autoScroll) return
    const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50

    if (isAtBottom) {
      userIsScrollingRef.current = false
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
      return
    }

    userIsScrollingRef.current = true
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    scrollTimeoutRef.current = setTimeout(() => {
      if (autoScroll) {
        scrollToBottom()
        userIsScrollingRef.current = false
      }
    }, 3000)
  }, [autoScroll, scrollToBottom, filteredLogs.length, hasMore, isLoadingMore, loadMoreLogs])

  useEffect(() => {
    return () => {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current)
      }
    }
  }, [])

  // Track container size
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver(entries => {
      setContainerHeight(entries[0].contentRect.height)
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  // Auto scroll on new logs
  useEffect(() => {
    if (autoScroll && !userIsScrollingRef.current) {
      scrollToBottom()
    }
  }, [filteredLogs.length, autoScroll, scrollToBottom])

  // Copy single log
  const handleCopyLog = useCallback((log) => {
    const timestamp = formatTimestamp(log.timestamp, settings.timeFormat)
    const text = `[${timestamp}] [${log.level}] ${log.message}`
    copyToClipboard(text, showToast)
  }, [settings.timeFormat, showToast])

  if (isLoading) {
    return (
      <div 
        id="log-viewer"
        className="flex-1 flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        <div className="flex items-center gap-3" style={{ color: 'var(--text-muted)' }}>
          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading logs...</span>
        </div>
      </div>
    )
  }

  if (!currentSessionId) {
    return (
      <div 
        id="log-viewer"
        className="flex-1 flex flex-col items-center justify-center text-center p-8"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        <div className="text-5xl mb-4 opacity-70">📦</div>
        <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          No Session Selected
        </h2>
        <p className="text-sm max-w-[280px]" style={{ color: 'var(--text-muted)' }}>
          Select a session from the sidebar to view logs
        </p>
      </div>
    )
  }

  if (filteredLogs.length === 0) {
    return (
      <div 
        id="log-viewer"
        className="flex-1 flex flex-col items-center justify-center p-8 text-center"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        <div className="text-4xl mb-3 opacity-60">🔍</div>
        <p style={{ color: 'var(--text-muted)' }}>
          {logs.length === 0 ? 'No logs in this session' : 'No logs match the current filters'}
        </p>
      </div>
    )
  }

  return (
    <div 
      id="log-viewer"
      ref={containerRef}
      onScroll={handleScroll}
      className={`flex-1 overflow-auto scrollbar-thin ${settings.scanLines ? 'scan-lines' : ''}`}
      style={{ backgroundColor: 'var(--bg-primary)', fontSize: `${settings.fontSize}px` }}
    >
      <div className="px-2 py-1">
        {isLoadingMore && (
          <div className="flex items-center justify-center gap-2 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            <span>Loading older logs...</span>
          </div>
        )}
        <div style={{ paddingTop: `${paddingTop}px`, paddingBottom: `${paddingBottom}px` }}>
          {visibleLogs.map((log, index) => (
            <LogLine 
              key={`${log.id || log.timestamp}-${index}`}
              log={log}
              searchTerm={searchTerm}
              timeFormat={settings.timeFormat}
              onCopy={handleCopyLog}
            />
          ))}
        </div>
      </div>
    </div>
  )
})

const levelColors = {
  ERROR: { color: 'var(--color-error)', bg: 'rgba(255,85,85,0.12)' },
  WARN: { color: 'var(--color-warn)', bg: 'rgba(255,184,108,0.12)' },
  INFO: { color: 'var(--color-info)', bg: 'rgba(139,233,253,0.12)' },
  DEBUG: { color: 'var(--color-debug)', bg: 'rgba(98,114,164,0.12)' },
  OTHER: { color: 'var(--text-muted)', bg: 'rgba(248,248,242,0.08)' }
}

const LogLine = memo(function LogLine({ log, searchTerm, timeFormat, onCopy }) {
  const level = log.level || 'OTHER'
  const levelStyle = levelColors[level] || levelColors.OTHER
  
  const istDate = convertToIST(log.timestamp)
  const dateStr = istDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const timeStr = formatTimestamp(log.timestamp, timeFormat)

  // Parse ANSI and highlight search term
  let message = parseAnsiColors(log.message)
  if (searchTerm) {
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    message = message.replace(regex, '<mark class="bg-yellow-400/70 text-black px-0.5 rounded-sm">$1</mark>')
  }

  return (
    <div 
      className="log-line group px-3 py-2.5 rounded-md hover:bg-[var(--color-hover)] transition-colors"
      style={{ borderLeft: `2px solid ${levelStyle.color}` }}
    >
      {/* Mobile layout: Timestamp + Level above message */}
      <div className="md:hidden flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="flex flex-col text-[11px] tabular-nums">
            <span style={{ color: 'var(--text-muted)' }}>{dateStr}</span>
            <span style={{ color: 'var(--text-muted)' }} className="opacity-60">{timeStr}</span>
          </div>
          
          <span 
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
            style={{ color: levelStyle.color, backgroundColor: levelStyle.bg }}
          >
            {level.slice(0, 5)}
          </span>
        </div>
        
        <button
          onClick={() => onCopy(log)}
          className="p-1.5 rounded-md opacity-100 md:opacity-0 group-hover:opacity-100 transition-all hover:bg-[var(--bg-tertiary)]"
          style={{ color: 'var(--text-muted)' }}
          title="Copy log"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
          </svg>
        </button>
      </div>

      {/* Desktop layout: Horizontal */}
      <div className="hidden md:flex items-start gap-3">
        {/* Timestamp + Level compact view */}
        <div className="flex-shrink-0 flex flex-col text-[11px] tabular-nums min-w-[4.5rem]">
          <span style={{ color: 'var(--text-muted)' }}>{dateStr}</span>
          <span style={{ color: 'var(--text-muted)' }} className="opacity-60">{timeStr}</span>
        </div>

        {/* Level Badge */}
        <span 
          className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded text-center min-w-[3rem]"
          style={{ color: levelStyle.color, backgroundColor: levelStyle.bg }}
        >
          {level.slice(0, 5)}
        </span>

        {/* Message */}
        <span 
          className="flex-1 break-words whitespace-pre-wrap text-[13px] leading-relaxed"
          style={{ color: 'var(--text-primary)' }}
          dangerouslySetInnerHTML={{ __html: message }}
        />

        {/* Copy Button */}
        <button
          onClick={() => onCopy(log)}
          className="flex-shrink-0 p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-all hover:bg-[var(--bg-tertiary)]"
          style={{ color: 'var(--text-muted)' }}
          title="Copy log"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
          </svg>
        </button>
      </div>

      {/* Mobile: Message (full width) */}
      <div className="md:hidden">
        <span 
          className="block break-words whitespace-pre-wrap text-[13px] leading-relaxed"
          style={{ color: 'var(--text-primary)' }}
          dangerouslySetInnerHTML={{ __html: message }}
        />
      </div>
    </div>
  )
})
